import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Bell } from "@/components/icons/";
import Popover from "@/components/ui/popover";

import { useUnreadCount } from "../../hooks/useUnreadCount";
import { notificationsApi } from "../../services/notifications.api";
import type { NotificationItem } from "../../types/notifications.types";
import { resolveNotificationRoute } from "../../utils/route-resolver";
import { useStore } from "@/store";

import styles from "./index.module.scss";

interface Props {
  /** Override polling interval (ms). Default 30s. */
  pollingIntervalMs?: number;
}

/**
 * NotificationBell
 *
 * Header-level bell icon with:
 *  - unread badge (polled every 30s via `useUnreadCount`)
 *  - dropdown of the 10 most recent notifications
 *  - per-item click: mark-as-read + navigate to the linked resource
 *  - "Mark all as read" and "View all" actions
 *
 * NOTE: Read/unread state is authoritative on the backend. Optimistic UI
 * updates are applied for snappiness but every action calls the API.
 */
const NotificationBell: React.FC<Props> = ({ pollingIntervalMs }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [recent, setRecent] = useState<NotificationItem[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const { count, refetch, decrement, reset } = useUnreadCount({
    intervalMs: pollingIntervalMs ?? 30_000,
  });

  const loadRecent = useCallback(async () => {
    setIsLoadingList(true);
    setListError(null);
    try {
      const res = await notificationsApi.listNotifications({
        page: 1,
        limit: 10,
      });
      setRecent(res.items ?? []);
    } catch {
      setListError(
        t("notifications.loadError"),
      );
    } finally {
      setIsLoadingList(false);
    }
  }, [t]);

  useEffect(() => {
    if (!isOpen) return;
    void loadRecent();
    // Rozet 30 saniyede bir poll ediliyor; dropdown açıldığında sayacı da
    // tazele. Aksi halde listede yeni bildirim görünürken rozet bir sonraki
    // poll'a kadar eski değerde kalıyordu.
    void refetch();
  }, [isOpen, loadRecent, refetch]);

  const handleItemClick = async (n: NotificationItem) => {
    const wasUnread = !n.readAt;
    setRecent((prev) =>
      prev.map((x) =>
        x.id === n.id && !x.readAt
          ? { ...x, readAt: new Date().toISOString() }
          : x,
      ),
    );
    if (wasUnread) decrement(1);

    try {
      if (wasUnread) await notificationsApi.markNotificationAsRead(n.id);
    } catch {
      // Revert optimistic state on failure.
      setRecent((prev) =>
        prev.map((x) => (x.id === n.id ? { ...x, readAt: n.readAt } : x)),
      );
      await refetch();
    }

    const route = resolveNotificationRoute(n, {
      isStaff: useStore.getState().isStaff,
    });
    setIsOpen(false);
    if (route) navigate(route);
  };

  const handleMarkAllAsRead = async () => {
    const prev = recent;
    setRecent((list) =>
      list.map((x) =>
        x.readAt ? x : { ...x, readAt: new Date().toISOString() },
      ),
    );
    reset();
    try {
      await notificationsApi.markAllNotificationsAsRead();
    } catch {
      setRecent(prev);
      await refetch();
    }
  };

  const formatRelative = (iso: string) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  };

  const panel = (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <h4>{t("notifications.title")}</h4>
        <button
          type="button"
          onClick={handleMarkAllAsRead}
          disabled={count === 0 && !recent.some((x) => !x.readAt)}
        >
          {t("notifications.markAllAsRead")}
        </button>
      </div>

      <div className={styles.panelList}>
        {isLoadingList && (
          <div className={styles.empty}>
            {t("common.loading")}
          </div>
        )}
        {!isLoadingList && listError && (
          <div className={styles.empty}>{listError}</div>
        )}
        {!isLoadingList && !listError && recent.length === 0 && (
          <div className={styles.empty}>
            {t("notifications.empty")}
          </div>
        )}
        {!isLoadingList &&
          !listError &&
          recent.map((n) => {
            const unread = !n.readAt;
            return (
              <div
                key={n.id}
                className={`${styles.item} ${unread ? styles.unread : ""}`}
                onClick={() => void handleItemClick(n)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    void handleItemClick(n);
                  }
                }}
              >
                {unread ? (
                  <div className={styles.itemDot} />
                ) : (
                  <div className={styles.itemDotPlaceholder} />
                )}
                <div className={styles.itemBody}>
                  {n.title && <h5 className={styles.itemTitle}>{n.title}</h5>}
                  <p className={styles.itemMessage}>{n.message}</p>
                  <div className={styles.itemMeta}>
                    {formatRelative(n.createdAt)}
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      <div className={styles.panelFooter}>
        <a
          onClick={(e) => {
            e.preventDefault();
            setIsOpen(false);
            navigate("/dashboard/notifications");
          }}
        >
          {t("notifications.viewAll")}
        </a>
      </div>
    </div>
  );

  return (
    <div className={styles.bellWrapper}>
      <Popover
        content={panel}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        position="bottom"
      >
        <button
          type="button"
          className={`${styles.bellButton} ${isOpen ? styles.active : ""}`}
          aria-label={t("notifications.title")}
          onClick={() => setIsOpen((v) => !v)}
        >
          <Bell width={22} height={22} />
          {count > 0 && (
            <span className={styles.badge}>{count > 99 ? "99+" : count}</span>
          )}
        </button>
      </Popover>
    </div>
  );
};

export default NotificationBell;
